"use client";

import { Suspense, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { signIn } from "@/lib/auth-client";
import {
  Bouchon,
  Btn,
  Icons,
  PageShell,
  TopBar,
} from "@/components/design";

function safeNext(next: string | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return "/home";
  return next;
}

function LoginForm() {
  const searchParams = useSearchParams();
  const callbackURL = safeNext(searchParams.get("next"));
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [error, setError] = useState<string | null>(null);

  async function handleGoogle() {
    setError(null);
    try {
      await signIn.social({ provider: "google", callbackURL });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Google login failed");
    }
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setStatus("sending");
    setError(null);
    try {
      const res = await signIn.magicLink({ email, callbackURL });
      if (res?.error) {
        setStatus("error");
        setError(res.error.message ?? "Magic link send failed");
      } else {
        setStatus("sent");
      }
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : "Magic link send failed");
    }
  }

  const sending = status === "sending";
  const sent = status === "sent";

  return (
    <>
      <label
        className="mono block mb-2"
        style={{
          fontSize: 10,
          color: "var(--color-text-dim)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        E-mail
      </label>
      <form onSubmit={handleMagicLink} className="space-y-3">
        <div
          className="flex items-center gap-2.5 px-3.5"
          style={{
            height: 52,
            borderRadius: 6,
            background: "var(--color-surface-2)",
            border: "1px solid var(--color-border-strong)",
          }}
        >
          <Icons.Mail size={17} style={{ color: "var(--color-text-muted)" }} />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="peti@haverok.hu"
            required
            className="flex-1 bg-transparent border-0 outline-none font-body"
            style={{
              color: "var(--color-text)",
              fontSize: 15,
            }}
          />
        </div>
        <Btn
          type="submit"
          variant="primary"
          full
          disabled={sending || sent || !email}
          trail={!sent ? <Icons.ArrowRight size={17} /> : undefined}
        >
          {sending ? "Küldés…" : sent ? "✓ Elküldve" : "Magic-link küldése"}
        </Btn>
      </form>

      {sent && (
        <p
          className="mono mt-3 text-center"
          style={{
            fontSize: 12,
            color: "var(--color-acid)",
          }}
        >
          → nézd meg a postafiókod (10 perc érvényes)
        </p>
      )}
      {error && (
        <p
          className="mono mt-3 text-center"
          style={{
            fontSize: 12,
            color: "var(--color-danger)",
          }}
        >
          {error}
        </p>
      )}

      <div className="flex items-center gap-3 my-6">
        <div className="bm-divider flex-1" />
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: "var(--color-text-dim)",
            letterSpacing: "0.15em",
          }}
        >
          VAGY
        </span>
        <div className="bm-divider flex-1" />
      </div>

      <Btn
        type="button"
        onClick={handleGoogle}
        variant="secondary"
        full
        icon={
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#fff"
              d="M21.6 12.2c0-.7-.06-1.4-.2-2H12v3.8h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z"
            />
            <path
              fill="#fff"
              d="M12 22c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.7-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22z"
              opacity=".7"
            />
            <path
              fill="#fff"
              d="M6.4 14a6 6 0 0 1 0-3.9V7.4H3.1a10 10 0 0 0 0 9.2L6.4 14z"
              opacity=".5"
            />
            <path
              fill="#fff"
              d="M12 5.9c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.4l3.3 2.6C7.2 7.6 9.4 5.9 12 5.9z"
              opacity=".85"
            />
          </svg>
        }
      >
        Folytatás Google-lel
      </Btn>
    </>
  );
}

export default function LoginPage() {
  return (
    <PageShell
      bare
      top={
        <TopBar
          left={
            <Link
              href="/"
              aria-label="Vissza"
              style={{
                color: "var(--color-text-muted)",
                padding: 0,
                display: "flex",
              }}
            >
              <Icons.ArrowLeft size={20} />
            </Link>
          }
        />
      }
    >
      <div className="flex flex-col min-h-full px-2 lg:px-0 lg:w-full lg:max-w-[420px] lg:mx-auto lg:justify-center">
        {/* Card look on desktop only — transparent on mobile */}
        <div className="lg:bg-[var(--color-surface-1)] lg:border lg:border-[var(--color-border)] lg:rounded-[var(--radius-lg)] lg:p-8">
          <div className="flex justify-center mt-2 mb-7">
            <Bouchon size={72} />
          </div>

          <h1
            className="display"
            style={{
              fontSize: "clamp(34px, 10vw, 44px)",
              margin: 0,
              lineHeight: 0.95,
            }}
          >
            Lépj be a&nbsp;körbe.
          </h1>
          <p
            style={{
              color: "var(--color-text-muted)",
              fontSize: 14,
              marginTop: 10,
              marginBottom: 28,
            }}
          >
            Egy fiókhoz egy haver-kör. Nincs hirdetés, nincs feed-spam.
          </p>

          <Suspense fallback={<div className="h-44" />}>
            <LoginForm />
          </Suspense>
        </div>

        <div className="mt-auto text-center pt-6 lg:mt-0">
          <p
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--color-text-dim)",
              letterSpacing: "0.08em",
            }}
          >
            BELÉPÉSSEL ELFOGADOD AZ ÁSZF-ET.
          </p>
        </div>
      </div>
    </PageShell>
  );
}
